<template>
    <a-modal
        :visible="visible"
        :title="'Sayı Ayarları - ' + widget.title"
        width="600px"
        :footer="null"
        @cancel="$emit('close')"
        class="numbers-settings-modal"
        :destroyOnClose="true"
    >
        <div class="settings-container">
            <!-- Header Section -->
            <div class="header-section">
                <div class="left-actions">
                    <!-- Search Input -->
                    <a-input-search
                        v-model="searchQuery"
                        placeholder="Ara..."
                        style="width: 250px"
                        @search="handleSearch"
                        allowClear
                    />

                    <!-- Filter Button with Popover -->
                    <a-popover
                        placement="bottomLeft"
                        trigger="click"
                        v-model="filterVisible"
                        overlayClassName="table-filter-popover"
                    >
                        <template slot="content">
                            <TableFilter :fields="fields" :value="filters" @input="onFiltersUpdated" />
                        </template>
                        <SupsisButton :classNames="`${filters.length > 0 ? 'active-filters' : ''}`">
                            <i class="material-icons">filter_alt</i>
                            Filtre
                            <span v-if="filters.length > 0" class="filter-count">
                                {{ filters.length }}
                            </span>
                            <i class="material-icons clear-filters" @click.stop="clearFilters" v-if="filters.length"
                                >close</i
                            >
                        </SupsisButton>
                    </a-popover>
                </div>
            </div>

            <!-- Divider -->
            <a-divider style="margin: 12px 0" />

            <a-form :form="form" layout="vertical">
                <!-- Veri Kaynağı -->
                <a-form-item label="Veri Kaynağı">
                    <a-radio-group v-model="dataSource" button-style="solid">
                        <a-radio-button value="count">Kayıt Sayısı</a-radio-button>
                        <a-radio-button value="fields">Alan Seçimi</a-radio-button>
                    </a-radio-group>
                </a-form-item>

                <!-- Alan Seçimi -->
                <template v-if="dataSource === 'fields'">
                    <a-form-item label="Sayısal Alan(lar)">
                        <a-select
                            mode="multiple"
                            v-model="previewSettings.fields"
                            style="width: 100%"
                            placeholder="Alan seçiniz"
                        >
                            <a-select-option v-for="field in numericFields" :key="field._id" :value="field._id">
                                {{ field.name }}
                            </a-select-option>
                        </a-select>
                    </a-form-item>

                    <!-- Hesaplama Tipi -->
                    <a-form-item label="Hesaplama Tipi">
                        <a-radio-group v-model="previewSettings.calculationType" button-style="solid">
                            <a-radio-button value="sum">Toplam</a-radio-button>
                            <a-radio-button value="average">Ortalama</a-radio-button>
                            <a-radio-button value="median">Medyan</a-radio-button>
                            <a-radio-button value="min">Minimum</a-radio-button>
                            <a-radio-button value="max">Maksimum</a-radio-button>
                        </a-radio-group>
                    </a-form-item>
                </template>

                <div class="preview-and-format">
                    <!-- Önizleme -->
                    <div class="preview-section">
                        <div class="preview-label">Önizleme:</div>
                        <div class="preview-value">
                            <template v-if="unitValue">
                                <span v-if="previewSettings.customization.unit.position === 'left'" class="unit">
                                    {{ unitValue }}
                                </span>
                            </template>
                            <span class="value" :style="{ color: valueColor }">{{ formattedPreviewValue }}</span>
                            <template v-if="unitValue">
                                <span v-if="previewSettings.customization.unit.position === 'right'" class="unit">
                                    {{ unitValue }}
                                </span>
                            </template>
                        </div>
                    </div>

                    <!-- Ondalık Format -->
                    <div class="decimal-format">
                        <div class="decimal-buttons">
                            <a-button 
                                @click="decreaseDecimal" 
                                :disabled="previewSettings.decimalFormat <= 0"
                                class="decimal-button"
                            >
                                <i class="material-icons">remove</i>
                            </a-button>
                            <a-button 
                                @click="increaseDecimal" 
                                :disabled="previewSettings.decimalFormat >= 10"
                                class="decimal-button"
                            >
                                <i class="material-icons">add</i>
                            </a-button>
                        </div>
                        <div class="decimal-value">Ondalık: {{ previewSettings.decimalFormat }}</div>
                    </div>
                </div>

                <!-- Birim Ayarları -->
                <div class="unit-settings-container">
                    <div class="unit-row">
                        <div class="setting-label">Birim:</div>
                        <div class="setting-content">
                            <a-radio-group :value="selectedUnit" button-style="solid">
                                <a-radio-button value="none" @click="handleUnitChange('none')">Birim Yok</a-radio-button>
                                <a-radio-button value="$" @click="handleUnitChange('$')">$</a-radio-button>
                                <a-radio-button value="€" @click="handleUnitChange('€')">€</a-radio-button>
                                <a-radio-button value="₺" @click="handleUnitChange('₺')">₺</a-radio-button>
                                <a-radio-button value="custom" @click="handleUnitChange('custom')">Özel</a-radio-button>
                            </a-radio-group>
                            
                            <a-input
                                v-if="selectedUnit === 'custom'"
                                v-model="customUnit"
                                placeholder="Özel birim"
                                style="width: 120px"
                                @change="handleCustomUnitChange"
                            />
                        </div>
                    </div>

                    <div class="unit-row">
                        <div class="setting-label">Birim Pozisyonu:</div>
                        <div class="setting-content">
                            <a-radio-group 
                                v-model="previewSettings.customization.unit.position" 
                                button-style="solid"
                                class="position-buttons"
                                :disabled="selectedUnit === 'none'"
                            >
                                <a-radio-button value="left">Solda</a-radio-button>
                                <a-radio-button value="right">Sağda</a-radio-button>
                            </a-radio-group>
                        </div>
                    </div>

                    <div class="unit-row">
                        <div class="setting-label">Renk:</div>
                        <div class="setting-content">
                            <a-input
                                v-model="previewSettings.customization.color"
                                type="color"
                                style="width: 80px"
                            />
                        </div>
                    </div>
                </div>

                <!-- Kaydet/İptal Butonları -->
                <div class="form-actions">
                    <a-button @click="$emit('close')">İptal</a-button>
                    <a-button type="primary" @click="handleSave" :loading="saving">Kaydet</a-button>
                </div>
            </a-form>
        </div>
    </a-modal>
</template>

<script>
import Vue from "vue";
import { TABLE_API } from "../../../../../../../api/table";
import _ from "lodash";
import TableFilter from "../../../components/filter/TableFilter.vue";
import SupsisButton from "../../../../../Settings/components/SupsisButton.vue";

export default Vue.extend({
    name: "NumbersSettingsModal",
    components: {
        TableFilter,
        SupsisButton
    },
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        widget: {
            type: Object,
            required: true,
        },
        fields: {
            type: Array,
            required: true,
        },
        table: {
            type: Object,
            required: true,
        },
        upstreamFilters: {
            type: Object,
            default: () => ({filters: [], searchQuery: ""}),
        },
    },
    data() {
        const settings = _.cloneDeep(this.widget.settings || {
            calculationType: "count",
            fields: [],
            decimalFormat: 0,
            customization: {
                unit: {
                    value: "none",
                    position: "right"
                },
                color: "#1890ff"
            }
        });

        return {
            form: this.$form.createForm(this),
            previewSettings: settings,
            saving: false,
            selectedUnit: settings.customization.unit.value || "none",
            customUnit: "",
            dataSource: settings.calculationType === "count" ? "count" : "fields",
            filters: _.cloneDeep(this.widget.settings?.filters || []),
            searchQuery: _.cloneDeep(this.widget.settings?.searchQuery || ""),
            filterVisible: false
        };
    },
    computed: {
        numericFields() {
            return this.fields.filter(field => field.valueType === "number");
        },
        unitValue() {
            if (this.selectedUnit === "none") return null;
            return this.selectedUnit === "custom" ? this.customUnit : this.selectedUnit;
        },
        formattedPreviewValue() {
            // Sabit bir sayı üzerinden gösterim yapıyoruz: 1234.56789
            const baseNumber = 1234.56789;
            const decimalFormat = this.previewSettings.decimalFormat;
            
            // Sayıyı string'e çevirip nokta ile bölelim
            const [integerPart, decimalPart = ''] = baseNumber.toString().split('.');
            
            // Decimal kısmını istenilen uzunluğa getir (0'lar ile doldur)
            const paddedDecimal = (decimalPart + '0'.repeat(decimalFormat)).slice(0, decimalFormat);
            
            // Eğer decimal format 0 ise sadece tam sayı kısmını, değilse nokta ile birleştir
            const formattedNumber = decimalFormat === 0 
                ? integerPart 
                : `${integerPart}.${paddedDecimal}`;

            // Binlik ayracı için toLocaleString kullan
            return Number(formattedNumber).toLocaleString('tr-TR');
        },
        valueColor() {
            return this.previewSettings.customization.color;
        }
    },
    methods: {
        handleUnitChange(value) {
            console.log("handleUnitChange",value);
            this.selectedUnit = value;
            if (value === "none") {
                this.$set(this.previewSettings.customization.unit, "value", null);
            } else if (value === "custom") {
                this.customUnit = this.customUnit || "";
                this.$set(this.previewSettings.customization.unit, "value", this.customUnit);
            } else {
                this.$set(this.previewSettings.customization.unit, "value", value);
            }
        },
        handleCustomUnitChange(e) {
            this.customUnit = e.target.value;
            this.$set(this.previewSettings.customization.unit, "value", e.target.value);
        },
        increaseDecimal() {
            if (this.previewSettings.decimalFormat < 10) {
                this.previewSettings.decimalFormat++;
            }
        },
        decreaseDecimal() {
            if (this.previewSettings.decimalFormat > 0) {
                this.previewSettings.decimalFormat--;
            }
        },
        async handleSave() {
            try {
                this.saving = true;
                if (this.dataSource === "count") {
                    this.previewSettings.calculationType = "count";
                    this.previewSettings.fields = [];
                } else if (!this.previewSettings.calculationType) {
                    this.previewSettings.calculationType = "sum";
                }

                const updatedSettings = {
                    ...this.previewSettings,
                    filters: this.filters,
                    searchQuery: this.searchQuery
                };

                await this.$emit("save", updatedSettings);
            } catch (error) {
                console.error("Ayarlar kaydedilirken hata:", error);
                this.$message.error("Ayarlar kaydedilirken bir hata oluştu");
            } finally {
                this.saving = false;
            }
        },
        onFiltersUpdated(newFilters) {
            this.filters = newFilters;
            this.updatePreview();
        },
        clearFilters() {
            this.filters = [];
            this.updatePreview();
        },
        handleSearch(value) {
            this.searchQuery = value;
            this.updatePreview();
        },
    },
    created() {
        // Mevcut birim ayarını seç
        const currentUnit = _.get(this.widget, "settings.customization.unit.value");
        this.selectedUnit = currentUnit || "none";
        if (currentUnit && !["none", "$", "€", "₺"].includes(currentUnit)) {
            this.selectedUnit = "custom";
            this.customUnit = currentUnit;
        }
    },
    watch: {
        previewSettings: {
            handler(newSettings) {
                // Preview değeri computed property ile hesaplanıyor artık
                // Bu watch'a gerek kalmadı
            },
            deep: true
        },
        dataSource: {
            handler(newValue) {
                if (newValue === "count") {
                    this.previewSettings.calculationType = "count";
                    this.previewSettings.fields = [];
                } else if (!this.previewSettings.calculationType) {
                    this.previewSettings.calculationType = "sum";
                }
            },
            immediate: true
        }
    }
});
</script>

<style scoped lang="scss">
.numbers-settings-modal {
    .settings-container {
        .header-section {
            padding: 0 0 12px 0;
            .left-actions {
                display: flex;
                gap: 12px;
                align-items: center;
            }
        }

        .filter-count {
            margin-left: 4px;
            background-color: rgba(0, 0, 0, 0.2);
            padding: 2px 4px;
            border-radius: 4px;
            font-size: 12px;
            display: inline-flex;
            align-items: center;
        }

        .clear-filters {
            font-size: 14px;
            margin-left: 2px;
            cursor: pointer;
        }

        .preview-and-format {
            display: flex;
            align-items: center;
            gap: 24px;
            margin: 24px 0;
            padding: 16px;
            background: #fafafa;
            border-radius: 4px;

            .preview-section {
                flex: 1;

                .preview-label {
                    font-size: 14px;
                    color: rgba(0, 0, 0, 0.45);
                    margin-bottom: 8px;
                }

                .preview-value {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;

                    .value {
                        font-size: 24px;
                        font-weight: bold;
                        color: #1890ff;
                    }

                    .unit {
                        font-size: 16px;
                        color: #666;
                    }
                }
            }

            .decimal-format {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 8px;

                .decimal-buttons {
                    display: flex;
                    gap: 4px;

                    .decimal-button {
                        width: 32px;
                        height: 32px;
                        padding: 0;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        
                        .material-icons {
                            font-size: 18px;
                            line-height: 1;
                        }
                    }
                }

                .decimal-value {
                    font-size: 14px;
                    color: rgba(0, 0, 0, 0.65);
                }
            }
        }

        .unit-settings-container {
            display: flex;
            flex-direction: column;
            gap: 16px;

            .unit-row {
                display: flex;
                align-items: center;
                gap: 16px;

                .setting-label {
                    width: 120px;
                    font-size: 14px;
                    color: rgba(0, 0, 0, 0.85);
                }

                .setting-content {
                    display: flex;
                    gap: 8px;
                    align-items: center;
                }
            }
        }

        .form-actions {
            margin-top: 24px;
            display: flex;
            justify-content: flex-end;
            gap: 8px;
        }
    }
}

:deep(.ant-radio-group) {
    .material-icons {
        font-size: 18px;
        margin-right: 4px;
        vertical-align: -4px;
    }
}
</style> 